import CaptchaAliyun from "./CaptchaAliyun";
import { CaptchaCloudflare } from "./CaptchaCloudflare";
import { useEffect, useState } from "react";
import useCountDown from "@hooks/useCountDown";
import { useTranslation } from "react-i18next";
import commonservice from "@apis/commonservice";
import message from "@utils/message";
import { useAppSelector } from "@hooks/redux";
import report from "@report";

interface SendCodeProps {
  isEmail?: boolean;
  isAliyunCaptcha?: boolean;
  scene: "signup" | "login" | "reset" | "verify" | "resetPassword";
  data: {
    email?: string;
    countryCode?: string;
    phoneNumber?: string;
  };
  getValid?: () => boolean;
  getData?: () => object;
}

const COUNTDOWN_TIME = 60000;

export const SendCode = ({ isEmail, isAliyunCaptcha, scene, data, getValid, getData }: SendCodeProps) => {
  const { language } = useAppSelector((state) => state.globalReducer);
  const reqData = data || getData?.();
  const { t } = useTranslation();
  const [targetDate, setTargetDate] = useState<number>(0);
  const [visible, setVisible] = useState(false);
  const isValid = getValid?.() !== false;

  const needCaptcha = !isEmail;

  function specialHandleID(text: string) {
    if (language === "id-ID") {
      if (isEmail) {
        return text === "Verification code" ? "Kode via Email" : "Kirim";
      }
      if (!isEmail) {
        return text === "Verification code" ? "Kode via WhatsApp" : "Kirim";
      }
    }
    return t(text);
  }

  const [countdown] = useCountDown({
    targetDate,
    onEnd: () => {
      sessionStorage.removeItem("captcha_countdown_time");
      setTargetDate(0);
    },
  });

  useEffect(() => {
    if (countdown < 1000 && countdown >= 0) {
      sessionStorage.removeItem("captcha_countdown_time");
    } else {
      sessionStorage.setItem("captcha_countdown_time", JSON.stringify(countdown));
    }
  }, [countdown]);

  if (countdown !== 0) {
    return <div className="cursor-default">{`${t("Resend code")} ${Math.round(countdown / 1000)}s`}</div>;
  } else {
    if (needCaptcha) {
      if (isAliyunCaptcha) {
        return (
          <>
            {!isValid && (
              <div className="text-kds-fg-soft cursor-default relative ">{specialHandleID("Send Code")}</div>
            )}
            {isValid && (
              <CaptchaAliyun
                key={`${reqData.email}${reqData.countryCode}${reqData.phoneNumber}`}
                isValid={isValid}
                captchaVerifyCallback={async (token: string) => {
                  try {
                    await commonservice.sendSmsCode({
                      scene,
                      response: token,
                      ...reqData,
                      captchaType: "aliyun",
                    });
                    setTargetDate(Date.now() + COUNTDOWN_TIME);
                    return { captchaResult: true };
                  } catch (err) {
                    if (err === "Sorry, something went wrong, you can try again later.") {
                      // 认证失败
                      return { captchaResult: false };
                    } else {
                      // 其他业务异常，隐藏验证码
                      setTimeout(() => {
                        message.error(err);
                      }, 2000);
                      return { captchaResult: true };
                    }
                  }
                }}
              >
                {specialHandleID("Send Code")}
              </CaptchaAliyun>
            )}
          </>
        );
      } else {
        return (
          <>
            <CaptchaCloudflare
              visible={visible}
              setVisible={setVisible}
              captchaVerifyCallback={async (token: string) => {
                setTargetDate(Date.now() + COUNTDOWN_TIME);
                try {
                  await commonservice.sendSmsCode({
                    scene,
                    response: token,
                    ...reqData,
                    captchaType: "cloudflare",
                  });
                  setVisible(false);
                } catch (err) {
                  setTargetDate(0);
                  console.log(err);
                  if (err === "Sorry, something went wrong, you can try again later.") {
                    // 认证失败
                  } else {
                    // 其他业务异常，隐藏验证码
                    message.error(err);
                    setVisible(false);
                  }
                }
              }}
            ></CaptchaCloudflare>
            <div
              onClick={() => {
                if (isValid) {
                  setVisible(true);
                  report.event("cloudflare_send_code");
                }
              }}
              className={isValid ? "" : "text-kds-fg-soft cursor-default"}
            >
              {specialHandleID("Send Code")}
            </div>
          </>
        );
      }
    } else {
      if (!isValid) {
        return <div className="text-kds-fg-soft cursor-default">{specialHandleID("Send Code")}</div>;
      }

      return (
        <div
          onClick={async () => {
            try {
              await commonservice.sendSmsCode({ scene, response: "token", ...reqData });
              setTargetDate(Date.now() + COUNTDOWN_TIME);
            } catch (err) {
              message.error(err);
            }
          }}
        >
          {specialHandleID("Send Code")}
        </div>
      );
    }
  }
};
